import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { usePublicProducerFleaMarkets } from '@/hooks/usePublicFleaMarkets';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { formatOccurrenceRangeSv } from '@/lib/fleaMarkets/occurrences';
import { derivePreviewText } from '@/lib/text';
import { useTranslate } from '@/i18n';
import type { PublicFleaMarketRow } from '@/types/fleaMarkets';

interface ProducerFleaMarketsProps {
  producerId?: string;
  title?: string;
  maxItems?: number;
}

export function ProducerFleaMarkets({
  producerId,
  title,
  maxItems = 3,
}: ProducerFleaMarketsProps) {
  const t = useTranslate();
  const heading = title ?? t("producer.flea.heading");
  const { data: markets, isLoading } = usePublicProducerFleaMarkets(producerId);

  if (!producerId || isLoading || !markets || markets.length === 0) return null;

  const visible = markets.slice(0, maxItems);

  return (
    <section className="border-t border-border/30 pt-8">
      <div className="flex items-center gap-2 mb-1">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
          <CalendarDays className="h-4 w-4" />
        </div>
        <h2 className="font-display text-lg text-foreground">{heading}</h2>
      </div>
      <p className="text-xs text-muted-foreground ml-10 mb-4">Återkommande loppis med kommande tider</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((market) => (
          <ProducerFleaMarketCard key={market.flea_market_id} market={market} />
        ))}
      </div>
    </section>
  );
}

function ProducerFleaMarketCard({ market }: { market: PublicFleaMarketRow }) {
  const coverUrl = useCoverImageUrl('flea_market', market.flea_market_id, 640);
  const previewText = derivePreviewText(market.description, 120) || 'Loppis på gården.';

  const nextLabel = market.next_occurrence_at
    ? formatOccurrenceRangeSv(market.next_occurrence_at, market.next_occurrence_end_at)
    : 'Inga kommande tider just nu';

  return (
    <Link
      to={`/loppis/${market.flea_market_id}`}
      className="rounded-xl border border-border/60 bg-card/70 hover:bg-card transition-colors overflow-hidden"
    >
      <div className="aspect-[4/3] bg-muted">
        {coverUrl ? (
          <img src={coverUrl} alt={market.title} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">Ingen omslagsbild</div>
        )}
      </div>
      <div className="p-3 space-y-1.5">
        <h3 className="text-sm font-medium text-foreground line-clamp-2">{market.title}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2">{previewText}</p>
        <p className="text-xs text-foreground/80">Nästa: {nextLabel}</p>
      </div>
    </Link>
  );
}
